/**
 * SatisfactionConfigSection - Pesquisa de satisfação (CSAT)
 *
 * Envia uma pesquisa ao contato quando o atendimento é encerrado. Escala
 * fixa: menor = melhor (1=Ótimo … 5=Péssimo). As opções são sempre um
 * prefixo contíguo de 1..5 — remover sempre tira a última linha, adicionar
 * sempre acrescenta a próxima da lista de defaults. Ausente ou
 * enabled !== true = desligado (day-0 off).
 */

import React from 'react';
import type {
  UpdateChannelRequest,
  ChannelSatisfactionConfig,
  ChannelSatisfactionOption,
} from '@troia-v3/types';
import { DEFAULT_CHANNEL_SATISFACTION_CONFIG } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Switch } from '@/shared/components/ui/Switch';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Icon } from '@/shared/icons';

interface SatisfactionConfigSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: any) => void;
}

// Fonte única (`@troia-v3/types`): o nó "CSAT — Satisfação do Cliente" do
// workflow usa esta mesma pesquisa quando o canal nunca configurou a própria.
const DEFAULT_CONFIG: ChannelSatisfactionConfig = DEFAULT_CHANNEL_SATISFACTION_CONFIG;
const DEFAULT_OPTIONS: ChannelSatisfactionOption[] = DEFAULT_CHANNEL_SATISFACTION_CONFIG.options;

export const SatisfactionConfigSection: React.FC<SatisfactionConfigSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  const currentConfig: ChannelSatisfactionConfig =
    formData.satisfactionConfig || channel.satisfactionConfig || DEFAULT_CONFIG;

  const isEnabled = currentConfig.enabled;

  // Sempre envia o objeto completo — o PUT genérico substitui o sub-objeto inteiro
  const emit = (partial: Partial<ChannelSatisfactionConfig>) => {
    onFieldChange('satisfactionConfig', { ...currentConfig, ...partial });
  };

  const handleToggle = (checked: boolean) => {
    emit({ enabled: checked });
  };

  const handleMessageChange = (value: string) => {
    emit({ message: value });
  };

  const handleTimeoutChange = (value: string) => {
    const minutes = parseInt(value, 10);
    emit({ timeoutMinutes: Number.isFinite(minutes) ? minutes : 0 });
  };

  const handleEmojiChange = (value: ChannelSatisfactionOption['value'], newEmoji: string) => {
    const options = currentConfig.options.map((opt) =>
      opt.value === value ? { ...opt, emoji: newEmoji } : opt
    );
    emit({ options });
  };

  const handleLabelChange = (value: ChannelSatisfactionOption['value'], newLabel: string) => {
    const options = currentConfig.options.map((opt) =>
      opt.value === value ? { ...opt, label: newLabel } : opt
    );
    emit({ options });
  };

  const handleRemoveLastOption = () => {
    if (currentConfig.options.length <= 2) return;
    emit({ options: currentConfig.options.slice(0, -1) });
  };

  const handleAddOption = () => {
    if (currentConfig.options.length >= 5) return;
    const nextDefault = DEFAULT_OPTIONS[currentConfig.options.length];
    if (!nextDefault) return;
    emit({ options: [...currentConfig.options, nextDefault] });
  };

  return (
    <Card data-testid="satisfaction-config-section">
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="star" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            CSAT — Satisfação do Cliente
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Toggle de habilitação */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="satisfaction-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Enviar pesquisa de satisfação
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Envia uma pesquisa ao cliente quando o atendimento é encerrado
            </p>
          </div>
          <Switch
            id="satisfaction-toggle"
            checked={isEnabled}
            onChange={handleToggle}
            data-testid="satisfaction-toggle"
          />
        </div>

        {/* Configurações quando habilitado */}
        {isEnabled && (
          <div className="space-y-4 pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
            {/* Mensagem da pesquisa */}
            <div>
              <label
                htmlFor="satisfaction-message"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Mensagem da pesquisa
              </label>
              <Textarea
                id="satisfaction-message"
                value={currentConfig.message}
                onChange={(e) => handleMessageChange(e.target.value)}
                maxLength={1000}
                rows={3}
                data-testid="satisfaction-message"
              />
            </div>

            {/* Opções de resposta */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Opções de resposta
              </label>

              <div className="space-y-2">
                {currentConfig.options.map((option, index) => {
                  const isLast = index === currentConfig.options.length - 1;
                  return (
                    <div key={option.value} className="flex items-center gap-2">
                      <span
                        className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold flex-shrink-0"
                        style={{
                          backgroundColor: 'var(--theme-card-background)',
                          color: 'var(--theme-card-text)',
                          border: '1px solid var(--theme-card-border)',
                        }}
                      >
                        {option.value}
                      </span>
                      <Input
                        value={option.emoji}
                        onChange={(e) => handleEmojiChange(option.value, e.target.value)}
                        maxLength={16}
                        fullWidth={false}
                        className="w-[80px] text-center"
                        aria-label={`Emoji da opção ${option.value}`}
                        data-testid={`satisfaction-emoji-${option.value}`}
                      />
                      <Input
                        value={option.label}
                        onChange={(e) => handleLabelChange(option.value, e.target.value)}
                        maxLength={40}
                        aria-label={`Legenda da opção ${option.value}`}
                        data-testid={`satisfaction-label-${option.value}`}
                      />
                      {isLast && (
                        <button
                          type="button"
                          onClick={handleRemoveLastOption}
                          disabled={currentConfig.options.length <= 2}
                          className="p-1.5 rounded-lg transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: 'var(--theme-error-color)' }}
                          aria-label="Remover última opção"
                          data-testid={`satisfaction-remove-${option.value}`}
                        >
                          <Icon name="trash" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {currentConfig.options.length < 5 && (
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="mt-2 flex items-center gap-1.5 text-sm font-medium"
                  style={{ color: 'var(--theme-button-primary)' }}
                  data-testid="satisfaction-add-option"
                >
                  <Icon name="plus" className="w-4 h-4" />
                  Adicionar opção
                </button>
              )}

              <p className="text-xs mt-2" style={{ color: 'var(--theme-card-muted)' }}>
                1 é a melhor avaliação e 5 a pior. O cliente pode responder com o número, o emoji ou a legenda.
              </p>
            </div>

            {/* Janela para resposta */}
            <div>
              <label
                htmlFor="satisfaction-timeout"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Janela para resposta
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="satisfaction-timeout"
                  type="number"
                  min={5}
                  max={10080}
                  value={currentConfig.timeoutMinutes}
                  onChange={(e) => handleTimeoutChange(e.target.value)}
                  fullWidth={false}
                  className="w-[120px]"
                  data-testid="satisfaction-timeout"
                />
                <span className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>
                  minutos
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                Tempo após o encerramento do atendimento em que a resposta ainda é aceita
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

SatisfactionConfigSection.displayName = 'SatisfactionConfigSection';
